export type ImportAccountRowStatus =
  | 'pre_mapped'
  | 'mapped'
  | 'suggested'
  | 'pending';

export function isImportRowReadyToCommit(status: ImportAccountRowStatus): boolean {
  return status === 'pre_mapped' || status === 'mapped';
}

export function parseImportRowPercentage(row: ImportAccountRow): number | null {
  const pct = row.percentage.trim();
  if (pct === '') return null;
  const value = Number(pct);
  if (!Number.isFinite(value) || value < 0 || value > 100) return null;
  return value;
}

export function computeImportAmount(clientPl: number, percentage: number): number {
  return Math.round(((clientPl * percentage) / 100) * 100) / 100;
}

/** Rows that produce a non-zero import entry (matches backend preview inclusion). */
export function isImportRowPostable(row: ImportAccountRow): boolean {
  if (!isImportRowReadyToCommit(row.status) || !row.matchedPartyId) return false;
  const percentage = parseImportRowPercentage(row);
  if (percentage === null || row.clientPl === 0) return false;
  return computeImportAmount(row.clientPl, percentage) !== 0;
}

export interface ImportAccountRow {
  id: string;
  rowIndex: number;
  userNameRaw: string;
  loginId: string;
  displayName: string;
  cr: number;
  dr: number;
  clientPl: number;
  exposure: number;
  available: number;
  accountType: string;
  percentage: string;
  /** Display value for row party code input (e.g. "CODE - NAME"). */
  partyCodeInput: string;
  /** Last validated party code for this row. */
  validatedRowPartyCode: string;
  partyInputError?: string;
  matchedPartyId?: number;
  matchedPartyCode?: string;
  matchedPartyName?: string;
  status: ImportAccountRowStatus;
}

export interface ImportPreviewEntry {
  rowId: string;
  userNameRaw: string;
  matchedPartyCode: string;
  matchedPartyName: string;
  percentage: number;
  importAmount: number;
  debit: number;
  credit: number;
  partnerDebit?: number;
  partnerCredit?: number;
  settlementIsDebit: boolean;
}

export interface ParsedUserName {
  loginId: string;
  displayName: string;
  raw: string;
}

export interface ImportAccountPreMappedEntry {
  loginId?: string;
  userNameRaw: string;
  partyCode: string;
  partyId?: number;
  partyName?: string;
}

export interface ImportAccountUsernameMapping {
  id?: number;
  externalUsername?: string;
  externalDisplayName?: string;
  partyId?: number;
  partyCode?: string;
  partyName?: string;
  isActive?: boolean;
}

export interface ImportAccountFileUploadResponse {
  fileId: number;
  originalFileName: string;
  uploadedAt: string;
  status: string;
}

export interface ImportAccountParseRowResponse {
  rowIndex: number;
  userNameRaw?: string;
  loginId?: string;
  displayName?: string;
  cr?: number;
  dr?: number;
  clientPl?: number;
  exposure?: number;
  available?: number;
  accountType?: string;
  matchedPartyId?: number | null;
  matchedPartyCode?: string | null;
  matchedPartyName?: string | null;
  mappingSource?: string | null;
  rowStatus?: string | null;
  error?: string | null;
}

export interface ImportAccountParseResponse {
  fileId: number;
  rows: ImportAccountParseRowResponse[];
}

export interface ImportAccountPreviewApiEntry {
  rowIndex: number;
  loginId?: string;
  displayName?: string;
  matchedPartyId?: number;
  matchedPartyCode: string;
  matchedPartyName: string;
  percentage: number;
  available?: number;
  importAmount: number;
  settlementDebit?: number;
  settlementCredit?: number;
  rowPartyDebit: number;
  rowPartyCredit: number;
  partnerDebit?: number;
  partnerCredit?: number;
  rowStatus?: string;
}

export interface ImportAccountPreviewSummary {
  totalRows?: number;
  includedRows?: number;
  excludedRows?: number;
  errorRows?: number;
  totalSettlementDebit?: number;
  totalSettlementCredit?: number;
  totalPartnerDebit?: number;
  totalPartnerCredit?: number;
}

export interface ImportAccountPreviewResponse {
  importBatchId: number;
  importAccountFileId: number;
  clientBatchId: string;
  summary?: ImportAccountPreviewSummary;
  entries: ImportAccountPreviewApiEntry[];
  errors?: Array<{ rowIndex: number; code: string; message: string }>;
}

export interface ImportAccountExecuteResponse {
  importBatchId: number;
  importAccountFileId: number;
  clientBatchId: string;
  postedAt: string;
  transactionIds: number[];
  isIdempotentReplay: boolean;
}
